import * as _ from 'underscore';

import {
  ButtonType,
  ButtonWidget,
  MenuItemInfo
} from '@tableau/widgets';
import * as React from 'react';
import { defaultMemoize } from 'reselect';

import { Messages } from '../../../codegen/Localize';
import {
  ButtonControlledChildComponentProps,
  ButtonControlledComponent,
  ButtonProps
} from './ButtonControlledComponent';
import { ButtonControlledFlyoutListMenu } from './ButtonControlledFlyoutListMenu';

export type ActionsMenuItem = {
  readonly label: string;
  readonly disabled?: boolean;
  readonly execute: () => void;
};

export type ActionsMenuProps = {
  readonly actions: ReadonlyArray<ActionsMenuItem>;
  readonly buttonLabel: string;
  readonly style?: React.CSSProperties;
};

export class ActionsMenu extends React.Component<ActionsMenuProps> {

  private readonly memoizedGetActionsMenuButtonComponent = defaultMemoize(getActionsMenuButtonComponent);
  private readonly memoizedGetActionsMenuChildComponent = defaultMemoize(getActionsMenuChildComponent);

  public render(): JSX.Element {
    const button = this.memoizedGetActionsMenuButtonComponent(this.props.buttonLabel, this.props.style);
    const child = this.memoizedGetActionsMenuChildComponent(this.props.actions);
    return (
      <span className="tb-box">
        <ButtonControlledComponent
          buttonComponent={button}
          childComponent={child}
          testId={'action-menu-test-id'}
        />
      </span>
    );
  }

}

type ActionsMenuButtonProps = ButtonProps & {
  readonly label: string;
  readonly style?: React.CSSProperties;
};

function ActionsMenuButton(props: ActionsMenuButtonProps): JSX.Element {
  return (
    <ButtonWidget
      buttonType={ButtonType.DarkText}
      handleClick={_.noop}
      children={props.label}
      testId={'action-menu-dropdown-button'}
      style={props.style}
    />
  );
}

function getActionsMenuButtonComponent(
  label: string,
  style?: React.CSSProperties
): React.ComponentType<ButtonProps> {
  return function actionsMenuButton(buttonProps: ButtonProps): JSX.Element {
    const fullProps: ActionsMenuButtonProps = { ...buttonProps, label, style };
    return <ActionsMenuButton {...fullProps} />;
  };
}

function getActionsMenuChildComponent(
  actions: ReadonlyArray<ActionsMenuItem>
): React.ComponentType<ButtonControlledChildComponentProps> {
  const menuItems: MenuItemInfo[] = actions.map((action, index) => ({
    disabled: action.disabled,
    displayValue: action.label,
    value: `${index}`
  }));
  const itemSelected = (value: string) => {
    const index = parseInt(value, 10);
    const action = actions[index];
    if (action) {
      action.execute();
    }
  };
  return function actionsMenuChild(props: ButtonControlledChildComponentProps): JSX.Element {
    return <ButtonControlledFlyoutListMenu
      onComponentDidMount={_.noop}
      menuItems={menuItems}
      onMenuItemSelected={itemSelected}
      anchorElement={props.anchorElement}
      closeMe={props.closeMe}
      testId={'action-menu-wrapper'}
    />;
  };
}
